import React from 'react';
import { Link } from 'react-router-dom';

const ServiceCard = ({ title, description, icon, link, color }) => {
  return (
    <Link to={link} className="block h-full">
      <div className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow h-full border-t-4 ${color}`}>
        <div className="p-6 flex flex-col h-full">
          <div className="flex items-center mb-4">
            <div className="text-4xl text-gray-400 mr-4">{icon}</div>
            <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
          </div>
          <p className="text-gray-600 mb-6 flex-grow">{description}</p>
          <div className="mt-auto flex items-center text-primary font-medium">
            Learn more
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
            </svg>
          </div>
        </div>
      </div>
    </Link>
  );
};

const AllServices = () => {
  const services = [
    {
      title: "Platform Engineering",
      description: "Improve productivity of your technology teams by getting the best out of infrastructure automation and creating developer-friendly self-service platforms.",
      icon: "🔧",
      link: "/platform-engineering",
      color: "border-blue-500"
    },
    {
      title: "Managed OpenShift",
      description: "Enable developers to control and configure the entire cloud development loop with enterprise-grade Kubernetes distribution from Red Hat.",
      icon: "☁️",
      link: "/managed-openshift",
      color: "border-red-500"
    },
    {
      title: "Kubernetes Consulting",
      description: "Get a jumpstart on the complex world of Kubernetes with expert guidance and implementation support for your cloud-native journey.",
      icon: "⚙️",
      link: "/kubernetes-consulting",
      color: "border-purple-500"
    },
    {
      title: "Multi Tenant Operator",
      description: "Automate multi-tenancy management for your Kubernetes clusters with advanced isolation and security between workloads.",
      icon: "🛡️",
      link: "/multi-tenant-operator",
      color: "border-green-500"
    }
  ];

  const solutions = [
    {
      title: "Cloud Migration",
      description: "Migrate your applications and infrastructure to the cloud with minimal disruption and maximum efficiency.",
      icon: "🚀",
      link: "#",
      color: "border-orange-500"
    },
    {
      title: "Microservices Transformation",
      description: "Transform monolithic applications into scalable, maintainable microservices architecture.",
      icon: "📦",
      link: "#",
      color: "border-teal-500"
    },
    {
      title: "CI/CD Implementation",
      description: "Implement robust continuous integration and continuous delivery pipelines for faster, more reliable software delivery.",
      icon: "⚡",
      link: "#",
      color: "border-yellow-500"
    },
    {
      title: "Observability Solutions",
      description: "Gain deep insights into your applications and infrastructure with comprehensive monitoring, logging, and tracing.",
      icon: "👁️",
      link: "#",
      color: "border-indigo-500"
    }
  ];

  const industries = [
    {
      title: "Financial Services",
      description: "Secure, compliant, and scalable solutions for banks, fintech, and insurance companies.",
      icon: "💰",
      link: "#",
      color: "border-emerald-500"
    },
    {
      title: "Healthcare",
      description: "HIPAA-compliant infrastructure and applications for healthcare providers and health tech companies.",
      icon: "🏥",
      link: "#",
      color: "border-sky-500"
    },
    {
      title: "Retail & E-commerce",
      description: "Scalable platforms that handle peak traffic and provide seamless customer experiences.",
      icon: "🛒",
      link: "#",
      color: "border-pink-500"
    },
    {
      title: "Manufacturing",
      description: "IoT and edge computing solutions that optimize operations and enable smart manufacturing.",
      icon: "🏭",
      link: "#",
      color: "border-amber-500"
    }
  ];

  return (
    <div className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Our Services and Offerings</h1>
            <p className="text-gray-600 max-w-3xl mx-auto">
              We help organizations navigate the cloud-native landscape with expert services 
              designed to accelerate your journey and maximize your success.
            </p>
          </div>
          
          <div className="mb-20">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-gray-800">Core Services</h2>
              <div className="h-1 flex-grow bg-gray-200 ml-4 rounded-full overflow-hidden">
                <div className="h-full bg-primary w-1/3 rounded-full"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <ServiceCard 
                  key={index}
                  title={service.title}
                  description={service.description}
                  icon={service.icon}
                  link={service.link}
                  color={service.color}
                />
              ))}
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-lg p-8 mb-20">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-4">End-to-End Platform Engineering</h2>
              <p className="text-blue-100 max-w-3xl mx-auto">
                We provide comprehensive platform engineering solutions that cover the entire 
                lifecycle from initial assessment to ongoing optimization.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <div className="bg-blue-900 w-12 h-12 rounded-full flex items-center justify-center mb-4 border-2 border-white">
                  <span className="text-white font-bold">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Assessment</h3>
                <p className="text-blue-100">Evaluate your current infrastructure, workflows, and pain points</p>
              </div>
              <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <div className="bg-blue-900 w-12 h-12 rounded-full flex items-center justify-center mb-4 border-2 border-white">
                  <span className="text-white font-bold">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Design</h3>
                <p className="text-blue-100">Create a tailored platform architecture aligned with your business goals</p>
              </div>
              <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <div className="bg-blue-900 w-12 h-12 rounded-full flex items-center justify-center mb-4 border-2 border-white">
                  <span className="text-white font-bold">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Implementation</h3>
                <p className="text-blue-100">Build and deploy your platform with best practices and automation</p>
              </div>
              <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <div className="bg-blue-900 w-12 h-12 rounded-full flex items-center justify-center mb-4 border-2 border-white">
                  <span className="text-white font-bold">4</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Support</h3>
                <p className="text-blue-100">Provide ongoing maintenance, optimization, and knowledge transfer</p>
              </div>
            </div>
          </div>
          
          <div className="mb-20">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-gray-800">Solutions</h2>
              <div className="h-1 flex-grow bg-gray-200 ml-4 rounded-full overflow-hidden">
                <div className="h-full bg-primary w-1/3 rounded-full"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {solutions.map((solution, index) => (
                <ServiceCard 
                  key={index}
                  title={solution.title}
                  description={solution.description}
                  icon={solution.icon}
                  link={solution.link}
                  color={solution.color}
                />
              ))}
            </div>
          </div>
          
          <div className="mb-20">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-gray-800">Industry Solutions</h2>
              <div className="h-1 flex-grow bg-gray-200 ml-4 rounded-full overflow-hidden">
                <div className="h-full bg-primary w-1/3 rounded-full"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {industries.map((industry, index) => (
                <ServiceCard 
                  key={index}
                  title={industry.title}
                  description={industry.description}
                  icon={industry.icon}
                  link={industry.link}
                  color={industry.color}
                />
              ))}
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-lg shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Ready to Start Your Cloud-Native Journey?</h2>
                <p className="text-gray-600 mb-6">
                  We're here to help you navigate the complexities of Kubernetes and cloud-native technologies. 
                  Let's discuss how our services can address your specific needs and accelerate your success.
                </p>
                <Link 
                  to="/contact-us" 
                  className="inline-block bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-3 rounded-md transition"
                >
                  Schedule a Consultation
                </Link>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Why Choose Fepsec</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span className="text-gray-600">Deep Kubernetes and cloud-native expertise</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span className="text-gray-600">Proven track record with enterprise clients</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span className="text-gray-600">Active contributors to open source</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span className="text-gray-600">Tailored solutions for your specific needs</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span className="text-gray-600">Focus on knowledge transfer and enablement</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllServices;
